/**
 * Medlemmer — one menu item, four tabs.  CA-39, CA-71, CA-72, CA-96; membership UX plan PR A (owner, 2026-10-15).
 *
 *   «Til behandling (n)»  every item waiting for a staff member, grouped by kind, each opening its own action inline;
 *                         a group with nothing in it is not drawn, and an empty inbox is one calm line
 *   «Register»            the register (a row opens the person's page, Person.tsx) and app users not linked
 *   «Import og brev»      the Solidus import, Samtykke (the member's own confirmation), the Digipost letters, Medlemsbevis
 *   «Oppsett»             who owns the fields, staff members' own app accounts, the monthly decision review
 *
 * The tabs are core's (admin-shell MEMBER_TABS): a tab with nothing this person may use is absent, and each card keeps
 * the permission its endpoint checks (core rule 20). «n» counts only what the viewer may act on (GET /membership-work).
 *
 * Words: «Samtykke» is the member's own confirmation; «Medlemsbevis» is the mosque's PDF; «bekreft» is kept for the
 * second person who confirms. Membership states use one vocabulary (lib/membership-status).
 *
 * - The import card is ABSENT for someone without members.import (admin-shell NAV `parts`, core rule 20).
 * - Preview first, then import (two clicks); an .xlsx is sent as it is, so Excel never gets the chance to turn a
 *   fødselsnummer into a rounded number. The fnr sentence depends on the BankID-matching switch (CA-96).
 * - Membership requests (owner, 2026-10-11): a signed-in person ASKS; a person with membership.decide approves
 *   (choosing the member in the register, with the same preview and verification as a link) or rejects (a reason
 *   code; the note is for staff only) — both two clicks. A link never approves an application (decision 3). When
 *   another account already holds the person, an administrator (the server's canOverride) may «Godkjenn likevel» with
 *   a reason — the staff link's own override, inside the approval (owner, 2026-10-15); everybody else is told so.
 * - The independent register (owner, Phase 1, 2026-10-11): an approval links an existing person OR registers a new
 *   one from what the applicant submitted (OA- number); a strong duplicate needs "this is a different person". An
 *   application that may concern a staff member is decided by someone else, and approving it needs a second person.
 *   The import is partial unless somebody ticks "complete export", and a row that strongly matches a person
 *   registered here is held back for "same person" / "new person".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Shell } from '../core';
import { can, download, errText, post, readUpload, send, type Body, type Sent } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { Body as CardBody, Btn, Card, DropZone, Field, LoadError, Loading, More, Msg, Tag, TwoStep } from '../components/ui';
import { fieldErrorToast } from '../lib/toast';
import { AccountIdentity, LinkPreview, MethodField, PROVIDER_NAME, PersonSearch } from '../components/people';
import { ConfirmDialog, Receipt, receiptTime, type ConfirmResult, type ReceiptData } from '../components/dialog';
import { REQUEST_NO } from '../lib/membership-status';
import { APPROVE_FIELDS, approvalErrors, type ApproveField } from '../lib/decision';
import { latestById } from '../lib/list';
import { osloDate, osloDay } from '../lib/format';
import { DecisionReview, JoiningQueue, Ownership, ReconciliationQueue, StaffAppAccounts, WithdrawalCount, WithdrawalHistory, WithdrawalQueue } from './Register';
import { PersonPage, RegisterTab } from './Person';
import { ConsentOverview, Letters } from './Consent';

/** Application signing states as staff see them. «Signert» is an e-ID signature on the application, not a qualified one. */
const SIGNING_NO: Record<string, string> = { signed: 'signert med BankID (ikke kvalifisert)', signer_mismatch: 'signert av en annen enn søkeren', awaiting_signer: 'signering pågår',
  validation_indeterminate: 'fil mottatt, signaturen er ikke kontrollert', signer_unverifiable: 'signert, men ikke kontrollert at det var søkeren',
  created: 'signering pågår', expired: 'signering utløpt', cancelled: 'signering avbrutt', rejected: 'signering avvist', failed: 'signering feilet', superseded: 'søknaden endret etter signering' };
/** States whose text already says a signed file is kept; for any other state `hasFile` means: the file from the signing is kept (2026-10-12). */
const SIGNING_KEPT = ['signed', 'signer_mismatch', 'validation_indeterminate', 'signer_unverifiable'];
/** Only an OPEN session still has a check to come; a final one with a file kept is just that — kept (review 2026-10-12, P2-1). */
const SIGNING_OPEN = ['created', 'awaiting_signer'];
/** The state as staff read it. A synthetic (mock-provider, test-only) signature is never called BankID (review 2026-10-12, P2-2). */
const signingText = (s: Body) => (s.state === 'signed' && s.synthetic ? 'TEST: syntetisk signatur (ikke ekte)' : SIGNING_NO[s.state] || s.state);
const importPermission = (Shell.NAV.find((n) => n.id === 'members')!.parts || {}).import;

/** The decoded hash path ('/medlemmer/person/…'); a malformed address is the tab's root. */
function hashRoute(): string {
  try { return decodeURIComponent(location.hash.replace(/^#/, '').split('?')[0] || '/medlemmer'); } catch { return '/medlemmer'; }
}

export function Members() {
  const [route, setRoute] = useState(hashRoute);
  useEffect(() => {
    const on = () => setRoute(hashRoute());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const { badges, refreshBadges } = useNav();
  const tabs = Shell.memberTabsFor(can);
  const { tab, personId } = Shell.memberRoute(route, can);
  const n = badges.members || 0;
  return (
    <>
      {/* Real links (each tab has an address, so a bookmark or the back button works); the current one is aria-current.
          (Back from the shell's section tabs, owner 2026-10-24: the sidebar is the one from before the redesign.) */}
      <nav className="mtabs" aria-label="Medlemmer">
        {tabs.map((t) => (
          <a key={t.id} href={'#' + t.route} className={t.id === tab ? 'on' : ''} aria-current={t.id === tab ? 'page' : undefined}>
            {t.label_no}{t.id === 'inbox' && n > 0 && <><span className="mtab-n" aria-hidden="true">{n}</span><span className="vh">, {n} venter på deg</span></>}
          </a>))}
      </nav>
      {tab === 'inbox' && <WorkInbox onChanged={refreshBadges} />}
      {tab === 'register' && (personId ? <PersonPage key={personId} id={personId} /> : <RegisterTab />)}
      {tab === 'import' && <ImportTab />}
      {tab === 'setup' && <>
        {can('members.link') && <Ownership />}
        {can('staff.app_account.declare') && <StaffAppAccounts />}
        {can('audit.read') && <DecisionReview />}
      </>}
    </>
  );
}

/* ------------------------------------------------------------------------------------- «Til behandling» ----- */
/**
 * One inbox. GET /membership-work says which groups have anything in them FOR THIS PERSON (`open`); a group with
 * nothing is not drawn, so a quiet week is one line instead of five empty cards. Each group is the queue it always
 * was, with its own actions inline. Rejections (when a person may apply again) and approved applications (to read the
 * signed application afterwards) are history, not work: folded at the end.
 */
function WorkInbox({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useLoad<Body>('/membership-work');
  // Receipts (Masjid UX redesign, ADM-03): what was just decided stays at the top until dismissed — the queue it came
  // from may be empty and gone by then.
  const [receipts, setReceipts] = useState<ReceiptData[]>([]);
  const [focusReceipt, setFocusReceipt] = useState<string | null>(null);
  const addReceipt = (r: ReceiptData) => { setReceipts((all) => [r, ...all.filter((x) => x.id !== r.id)].slice(0, 5)); setFocusReceipt(r.id); };
  // Review F10: one application at a time — while one is open, the other queues and the history are put away.
  const [appOpen, setAppOpen] = useState(false);
  const shown = receipts.map((r) => <Receipt key={r.id} r={r} focus={r.id === focusReceipt} onDismiss={() => setReceipts((all) => all.filter((x) => x.id !== r.id))} />);
  if (error) return <>{shown}<LoadError error={error} retry={reload} /></>;
  if (!data) return <>{shown}<Loading /></>;
  const open: Record<string, number> = data.open || {};
  const changed = () => { void reload(); onChanged(); };
  const any = Object.values(open).some((v) => v > 0);
  return (
    <>
      {shown}
      {!any && <p className="minbox-empty" role="status">Ingenting venter på deg. Innmeldinger som må sjekkes, og utmeldinger, dukker opp her.</p>}
      {open.applications > 0 && <Requests state="open" onChanged={changed} onReceipt={addReceipt} onOpen={setAppOpen} />}
      {!appOpen && <>
      {open.joinings > 0 && <JoiningQueue onChanged={changed} />}
      {open.withdrawals > 0 && <WithdrawalQueue onChanged={changed} />}
      {open.reconciliations > 0 && <ReconciliationQueue onChanged={changed} />}
      {open.confirmations > 0 && <Confirmations only="open" onChanged={changed} />}
      {can('membership.decide') && <div className="minbox-past">
        <More label="Avslåtte søknader, siste 30 dager"><RejectedRequests /></More>
        <More label="Godkjente innmeldinger"><Requests state="active" onChanged={changed} /></More>
      </div>}
      {/* MKK #11 (2026-10-29): every withdrawal received, its receipt, and the count for the state grant. */}
      {can('membership.withdrawal.record') && <div className="minbox-past">
        {can('membership.withdrawal.list') && <More label="Utmeldinger (alle mottatte)"><WithdrawalHistory /></More>}
        <More label="Telling for statstilskudd"><WithdrawalCount /></More>
      </div>}
      </>}
    </>
  );
}

/* --------------------------------------------------------------------------------------- «Import og brev» ----- */
function ImportTab() {
  const imp = useLoad<Body>(can('members.import') ? '/members/unlinked' : null);
  return (
    <>
      {(!importPermission || can(importPermission)) && (imp.error ? <LoadError error={imp.error} retry={imp.reload} />
        : !imp.data ? <Loading /> : <ImportCard data={imp.data} onImported={imp.reload} />)}
      {can('members.import') && <ConsentOverview />}
      {can('members.import') && <Letters />}
      {can('membership.decide') && <Confirmations />}
    </>
  );
}

function ImportCard({ data, onImported }: { data: Body; onImported: () => void }) {
  const [pending, setPending] = useState<Body | null>(null);
  const [lines, setLines] = useState<{ t: string; warn?: boolean }[] | null>(null);
  const [ackNeeded, setAckNeeded] = useState(false);
  const [ack, setAck] = useState(false);
  const [full, setFull] = useState(false);
  // A full export that would end more than 10 % of the import's members (QA A13): the number, ticked by a person, and
  // sent with the import — the server refuses without it. Null = no such warning on this preview.
  const [endCount, setEndCount] = useState<number | null>(null);
  const [endAck, setEndAck] = useState(false);
  const [held, setHeld] = useState<Body[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, Body>>({});
  const [msg, say] = useMsg();
  const input = useRef<HTMLInputElement>(null);
  const reset = () => { setPending(null); setLines(null); setAck(false); setAckNeeded(false); setFull(false); setEndCount(null); setEndAck(false); setHeld([]); setResolutions({}); if (input.current) input.current.value = ''; };

  async function preview(file: File) {
    say(''); setLines([{ t: 'Leser ' + file.name + ' …' }]);
    let body: Body;
    try { body = await readUpload(file); } catch (e) { reset(); return say((e as Error).message, 'err'); }
    await show(body, full, file.name);
  }
  // The preview is re-run when "complete export" is ticked, so what is committed is what was shown.
  async function show(body: Body, isFull: boolean, name = String(body.filename || '')) {
    const file = { name };
    const r = await post('/members/preview', { ...body, mode: isFull ? 'full' : 'partial' });
    if (!r.ok) { reset(); return say(errText(r.body) + ((r.body.errors || []).length ? ' ' + r.body.errors.slice(0, 3).map((e: Body) => 'Rad ' + e.row + ': ' + e.message).join(' · ') : ''), 'err'); }
    const b = r.body, out: { t: string; warn?: boolean }[] = [];
    out.push({ t: `${file.name}: ${b.rows} rader · ${b.summary_no}` });
    if (b.activeCount != null) out.push({ t: `${b.activeCount} er medlemmer i dag · ${b.endedCount} er utmeldt` });
    if (b.withPid) out.push({ t: `${b.withPid} kan logge inn med BankID (fødselsnummeret er gjort om til en nøkkel og lagres ikke).` });
    if (b.endedWarning_no) out.push({ t: b.endedWarning_no, warn: true });
    for (const e of (b.errors || []).slice(0, 5)) out.push({ t: 'Rad ' + e.row + ': ' + e.message, warn: true });
    for (const w of (b.warnings || []).slice(0, 8)) out.push({ t: w.message });
    if (b.modeNote_no) out.push({ t: b.modeNote_no });
    setPending(body); setLines(out); setAckNeeded((b.rejectedColumns || []).length > 0); setHeld(b.held || []);
    setEndCount(b.endConfirmNeeded ? Number(b.ended && b.ended.length) || 0 : null); setEndAck(false);
  }

  const i = data.lastImport;
  const last = i ? [
    'Sist importert: ' + (i.importedAt ? new Date(i.importedAt).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '') + (i.importedBy ? ' av ' + i.importedBy : ''),
    i.filename, `${data.memberCount} medlemmer · ${i.created} nye · ${i.ended} avsluttet`,
    (i.rejectedColumns || []).length ? 'Avviste kolonner: ' + i.rejectedColumns.join(', ') : null,
  ].filter(Boolean).join(' · ') : 'Medlemslisten er aldri importert.';

  return (
    <Card title="Import fra medlemsregister" hint="Last opp registereksporten (.xlsx); appen oppdaterer status, medlemskort og givehistorikk for personer derfra."
      more={<p>Et eksternt medlemsregister er en valgfri kilde. Personer registrert i Masjid endres aldri av en import. Ved ukentlig opplasting kan et nytt medlem vente opptil 7 dager på å se kortet sitt i appen — derfor bør medlemsregisteret levere en nattlig fil.</p>}>
      <CardBody>
        <div className="warnbox">{data.pidMatching
          ? <><b>Fødselsnummer lagres aldri.</b> BankID-kobling er slått på: har eksporten en kolonne med fødselsnummer, gjøres hvert nummer om til en innloggingsnøkkel i det øyeblikket filen leses, og selve nummeret kastes. Forhåndsvisningen sier hvor mange medlemmer BankID kan finne.</>
          : <><b>Fødselsnummer importeres aldri.</b> Kolonnen avvises hvis den er med i filen. Fnr blir værende i medlemsregisteret, sammen med den årlige innrapporteringen 1. mars.</>}</div>
        <DropZone title="Slipp medlemseksporten her, eller klikk for å velge fil" accept=".xlsx,.csv,.txt" onFile={preview} inputRef={input}>
          registereksporten (.xlsx) som den er — ikke åpne og lagre den i Excel først. Leses: Id, Navn, Mobil/Telefon, E-post, Fødselsdato, Innmeldt/Utmeldt dato.
        </DropZone>
        {lines && <div className="field note" style={{ marginTop: 12 }}>{lines.map((l, k) => <div key={k} className={l.warn ? 'warn' : ''}>{l.t}</div>)}</div>}
        {pending && (
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {ackNeeded && <label><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Jeg har sett at filen inneholder fødselsnummer</label>}
            <label title="Bare en komplett eksport kan avslutte noen. Medlemskap registrert i Masjid avsluttes aldri av en import.">
              <input type="checkbox" checked={full} onChange={(e) => { setFull(e.target.checked); void show(pending, e.target.checked); }} /> Dette er en komplett eksport av medlemsregisteret (de som mangler, avsluttes)</label>
            {endCount !== null && <label className="warnbox" style={{ margin: 0, flexBasis: '100%' }}><input type="checkbox" checked={endAck} onChange={(e) => setEndAck(e.target.checked)} />{' '}
              <b>Jeg vet at {endCount} medlemskap avsluttes</b> fordi de mangler i filen. Det kan ikke angres med en knapp.</label>}
            <TwoStep ghost={false} label="Importer" busyLabel="Importerer …" disabled={endCount !== null && !endAck}
              confirmLabel={full ? (endCount !== null ? `Klikk igjen: importer og avslutt ${endCount}` : 'Klikk igjen: importer og avslutt de som mangler') : 'Klikk igjen for å importere'} onConfirm={async () => {
              const r = await post('/members/commit', { ...pending, acknowledgeRejectedColumns: ack, mode: full ? 'full' : 'partial', resolutions,
                ...(endCount !== null && endAck ? { confirmEndedCount: endCount } : {}) });
              if (!r.ok) {
                // The file and the register moved apart since the preview: show the new number and ask again.
                if (r.body && r.body.code === 'confirm_ended_count') { setEndCount(Number(r.body.endedCount) || 0); setEndAck(false); }
                return say(errText(r.body), 'err');
              }
              const extra = [(r.body.held || []).length ? `${r.body.held.length} venter på gjennomgang` : '', r.body.linked ? `${r.body.linked} koblet til registrerte personer` : '',
                (r.body.conflicts || []).length ? `${r.body.conflicts.length} rader ble ikke skrevet (eies av Masjid)` : ''].filter(Boolean).join(' · ');
              say('Importert: ' + (r.body.summary || r.body.imported + ' rader') + (extra ? ' · ' + extra : ''), 'ok'); reset(); onImported();
            }} />
            <Btn ghost onClick={() => { reset(); say('Avbrutt. Ingenting er importert.', 'ok'); }}>Avbryt</Btn>
          </div>)}
        {held.length > 0 && <div className="warnbox" style={{ marginTop: 10 }}>
          <b>Venter på gjennomgang:</b> disse radene ligner sterkt på personer som er registrert i Masjid. Ingenting slås sammen av seg selv.
          {held.map((h) => <div key={h.solidusNo} style={{ marginTop: 6 }}>medl. {h.solidusNo} ({h.initials}) — {h.candidates.map((c: Body) => (
            <label key={c.memberId} style={{ marginRight: 10 }}><input type="radio" name={'held-' + h.solidusNo} checked={resolutions[h.solidusNo]?.memberId === c.memberId}
              onChange={() => setResolutions({ ...resolutions, [h.solidusNo]: { action: 'same_person', memberId: c.memberId } })} /> samme person som {c.memberNo}</label>))}
            <label><input type="radio" name={'held-' + h.solidusNo} checked={resolutions[h.solidusNo]?.action === 'new_person'}
              onChange={() => setResolutions({ ...resolutions, [h.solidusNo]: { action: 'new_person' } })} /> en annen person (blir loggført)</label></div>)}
          <div className="field note">Uavklarte rader importeres ikke. «Samme person» kobler medlemsnummeret fra registeret; personens OA-nummer beholdes.</div></div>}
        <Msg text={msg.text} tone={msg.tone} />
        <span className="field note" style={{ marginTop: 12, display: 'block' }}>{last}</span>
      </CardBody>
    </Card>
  );
}

/* ---------------- membership requests (owner, 2026-10-11) ---------------- */

/** The applications: waiting ones (the inbox), or approved ones (folded under it, to open a signed application afterwards). */
/**
 * The applications (Masjid UX redesign, 2026-10-23). A list of cards — who, when, what state in words, and whether
 * someone else must act — and one application at a time on its own page (`Decide`). The table row that opened the whole
 * decision inline (approve AND reject fields at once, ADM-02) is gone. An approved application opens the same page to
 * read what was signed.
 */
function Requests({ state: view, onChanged, onReceipt, onOpen }: { state: 'open' | 'active'; onChanged: () => void; onReceipt?: (r: ReceiptData) => void;
  /** Tells the inbox an application is open, so it can put the other queues away (review F10). */
  onOpen?: (open: boolean) => void }) {
  const { data, error, reload } = useLoad<Body>('/membership-requests' + (view === 'open' ? '' : '?state=' + view));
  const [openId, setOpenId] = useState<string | null>(null);
  const top = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const lastOpened = useRef<string | null>(null);
  // Review F2/F3: the opened application is scrolled into view (an approved one opens far down the page) and its name
  // takes the focus, so a keyboard or screen-reader user hears that the page changed; closing returns to its row.
  useEffect(() => {
    if (openId) { top.current?.scrollIntoView({ block: 'start' }); heading.current?.focus(); return; }
    const row = lastOpened.current ? document.querySelector<HTMLElement>(`.app-row[data-app="${CSS.escape(lastOpened.current)}"]`) : null;
    row?.focus();
  }, [openId]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  const done = (receipt?: ReceiptData) => { lastOpened.current = null; setOpenId(null); onOpen?.(false); reload(); onChanged(); if (receipt) onReceipt?.(receipt); window.scrollTo(0, 0); };
  const current = openId ? items.find((r) => r.id === openId) : null;
  if (current) return (
    <div ref={top} className="app-page">
      <p className="mback"><button type="button" className="link" onClick={() => { setOpenId(null); onOpen?.(false); }}>‹ {view === 'open' ? 'Alle innmeldinger' : 'Godkjente innmeldinger'}</button></p>
      <Card className="app-card">
        <div className="app-head">
          <h3 className="app-name" ref={heading} tabIndex={-1}><AccountIdentity account={current.account} registration={current.registration} exists={current.accountExists} /></h3>
          <StatusWords r={current} />
        </div>
        <p className="app-sub">Meldte seg inn {osloDay(current.state === 'removal_requested' ? current.removalRequestedAt : current.requestedAt)}
          {current.account?.identity?.provider ? ` · logget inn med ${PROVIDER_NAME[current.account.identity.provider] || current.account.identity.provider}` : ''}
          {current.signing ? ' · ' + signingPlain(current.signing) : ''}</p>
        {current.state === 'pending'
          ? <Decide r={current} methods={data.verificationMethods || []} reasons={data.rejectReasons || []} noteMax={data.noteMax || 500} onDone={done} />
          : current.state === 'removal_requested' && can('membership.withdrawal.record') ? <CompleteRemoval r={current} onDone={() => done()} />
          : current.member ? <div className="card-body"><p>Koblet til <b>{current.member.name}</b> · medl. {current.member.memberNo || current.member.solidusNo}.</p></div> : null}
        <div className="card-body app-details">
          <More label="Vis detaljer">
            {current.signing && <SignedApplication r={current} />}
            <ul className="app-facts">
              {current.registration?.statementVersion && <li>Innmeldingstekst: {current.registration.statementVersion}</li>}
              {current.signing && <li>Signering: {signingText(current.signing)}{current.signing.hasFile && !SIGNING_KEPT.includes(current.signing.state) ? ' · fil fra signeringen oppbevart' + (SIGNING_OPEN.includes(current.signing.state) ? ', kontroll venter' : '') : ''}</li>}
              {(current.earlier || []).length > 0 && <li>Tidligere innmeldinger: {current.earlier.map((e: string) => REQUEST_NO[e] || e).join(', ')}</li>}
              <li>Status i systemet: {REQUEST_NO[current.state] || current.state}</li>
            </ul>
          </More>
        </div>
      </Card>
    </div>
  );
  return (
    <Card title={view === 'open' ? (items.length === 1 ? '1 innmelding venter' : `${items.length} innmeldinger venter`) : 'Godkjente innmeldinger · ' + items.length}
      hint={view === 'open' ? 'Åpne en innmelding for å se hvem det er og avgjøre den.' : 'Åpne en godkjent innmelding for å lese det personen signerte.'}
      more={view === 'open' ? <p>Den som melder seg inn i appen, blir medlem med en gang. Her står bare det som må sjekkes av en person: et mulig treff i registeret, et fødselsnummer som er i bruk, en konto som allerede er koblet, eller en signatur som mangler. Et forslag fra registeret er ikke bevis. En utmelding i appen gjelder med en gang og havner ikke her.</p> : undefined}>
      {!items.length ? <div className="card-body">{view === 'open' ? 'Ingen innmeldinger venter.' : 'Ingen godkjente innmeldinger.'}</div> : (
        <ul className="app-list">{items.map((r) => (
          <li key={r.id}>
            <button type="button" className="app-row" data-app={r.id} onClick={() => { lastOpened.current = r.id; setOpenId(r.id); onOpen?.(true); }}>
              <span className="app-row-main">
                <b><AccountIdentity account={r.account} registration={r.registration} exists={r.accountExists} /></b>
                <span className="app-row-sub">Meldte seg inn {osloDay(r.state === 'removal_requested' ? r.removalRequestedAt : r.requestedAt)}
                  {r.member ? ` · koblet til ${r.member.name}` : ''}</span>
              </span>
              <StatusWords r={r} />
              <span className="app-row-go" aria-hidden="true">›</span>
            </button>
          </li>))}
        </ul>
      )}
    </Card>
  );
}

/** The applicant's name for an accessible label: what they gave, else what the sign-in said. */
const nameOf = (r: Body): string => (r.registration ? `${r.registration.firstName} ${r.registration.lastName}`
  : r.account?.identity?.name || r.account?.email || r.account?.phone || 'søkeren');
/** One status, in words — never more than one tag (ADM-11). The most important thing about the row wins. */
function StatusWords({ r }: { r: Body }) {
  if (r.state !== 'pending') return <Tag tone={r.state === 'active' ? 'green' : 'grey'}>{REQUEST_NO[r.state] || r.state}</Tag>;
  if (r.selfDecision) return <Tag tone="grey">Gjelder deg – en annen avgjør</Tag>;
  if (r.proposal) return <Tag tone="gold">{r.proposal.byMe ? 'Venter på en annen ansatt' : 'Venter på din bekreftelse'}</Tag>;
  if (r.staffFlagged) return <Tag tone="gold">Må bekreftes av en annen ansatt</Tag>;
  return <Tag tone="gold">{PENDING_NO[r.waitingFor] || 'Venter på avgjørelse'}</Tag>;
}
/** Why an application did not join by itself (2026-10-31) — the same reason the member's card shows. */
const PENDING_NO: Record<string, string> = { duplicate: 'Venter: mulig treff i registeret', national_id_held: 'Venter: fødselsnummeret er i bruk',
  awaiting_signature: 'Venter: BankID-signatur', review: 'Venter på avgjørelse' };
/** Signing in plain words for the line under the name; the exact state is under «Vis detaljer». */
function signingPlain(s: Body): string {
  if (s.state === 'signed' && s.synthetic) return 'testsignatur (ikke ekte)';
  if (s.state === 'signed') return 'signert med BankID';
  if (s.state === 'signer_mismatch') return 'signert av en annen enn søkeren';
  if (SIGNING_OPEN.includes(s.state)) return 'signering ikke fullført';
  return 'ikke signert';
}

/*
 * The signed application (owner, 2026-10-14): staff who decide may read the text the applicant signed with BankID and
 * download the signed file. The text is shown only when it is exactly what was signed (the server checks the hash);
 * otherwise the file is still there. Both are logged.
 */
function SignedApplication({ r }: { r: Body }) {
  const [shown, setShown] = useState<Body | null>(null);
  const [msg, say] = useMsg();
  const short = String(r.id).replace(/[^A-Za-z0-9-]/g, '').slice(0, 12);
  return (
    <div className="confirm">
      <div><b>Signert søknad</b> · {signingText(r.signing)}{r.signing.signedAt ? ' · ' + osloDate(r.signing.signedAt) : ''}</div>
      <div className="acts">
        <Btn ghost={!shown} aria-expanded={Boolean(shown)} onClick={async () => {
          if (shown) { setShown(null); return; }
          const x = await send(`/membership-requests/${encodeURIComponent(r.id)}/signed-application`);
          if (x.ok) { setShown(x.body); say('', ''); } else say(errText(x.body), 'err');
        }}>{shown ? 'Skjul teksten' : 'Vis signert søknad'}</Btn>
        {r.signing.hasFile && <Btn ghost onClick={async () => {
          const x = await download(`/v1/admin/membership-requests/${encodeURIComponent(r.id)}/signed-application/file`, `signert-soknad-${short}.xml`);
          if (!x.ok) say(x.message || 'Filen kunne ikke lastes ned.', 'err');
        }}>Last ned signert fil</Btn>}
      </div>
      {shown && (shown.text
        ? <pre dir="auto" style={{ whiteSpace: 'pre-wrap', fontSize: 13, margin: '8px 0 0' }}>{shown.text}</pre>
        : <div className="field note">Søknaden er endret etter signeringen, så teksten kan ikke vises her. Den signerte filen er uendret — last den ned.</div>)}
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/*
 * «Tillat ny søknad» (owner, 2026-10-11): rejected applications, when the person may ask again, and — for an
 * administrator — lifting the 30-day wait after ONE rejection. The rejection stands; nobody becomes a member by this.
 * The server checks everything again (permission, the newest request, the wait, self-action).
 */
const FIELD_NO: Record<string, string> = { firstName: 'Fornavn', lastName: 'Etternavn', birthDate: 'Fødselsdato', phone: 'Telefon', email: 'E-post' };
const day10 = (v?: string | null) => (v ? new Date(v).toLocaleDateString('nb-NO', { timeZone: 'Europe/Oslo' }) : '');
function RejectedRequests() {
  const { data, error, reload } = useLoad<Body>('/membership-requests?state=rejected');
  const [openId, setOpenId] = useState<string | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  // Still waiting, or lifted in the last 30 days (a lifted one drops out after that, like any other).
  const recent = (at?: string | null) => Boolean(at && Date.now() - new Date(at).getTime() < 30 * 864e5);
  const items: Body[] = (data.items || []).filter((r: Body) => r.reapply && (r.reapply.reapplyAfter || (r.reapply.release && recent(r.reapply.release.at))));
  return (
    <Card title={'Avslåtte søknader · ' + items.length} hint="Søkeren kan søke igjen fra datoen som står."
      more={<p>Listen viser søknader avslått de siste 30 dagene. En administrator kan tillate ny søknad med en gang — avslaget står, og ingen blir medlem av det.</p>}>
      {!items.length ? <div className="card-body">Ingen avslåtte søknader venter på ny søknad.</div> : (
        <table className="t">
          <thead><tr><th scope="col">Søker</th><th scope="col">Avslått</th><th scope="col">Kan søke igjen</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.map((r) => {
            const who = <AccountIdentity account={r.account} registration={r.registration} exists={r.accountExists} />;
            const rel = r.reapply.release;
            return (
              <React.Fragment key={r.id}>
                <tr>
                  <td>{who}</td>
                  <td>{day10(r.decidedAt)}</td>
                  <td>{rel ? <>Tillatt {day10(rel.at)}{rel.byName ? ' av ' + rel.byName : ''}</> : <>fra {day10(r.reapply.reapplyAfter)}</>}</td>
                  <td>{r.reapply.canAllow && <Btn ghost={openId !== r.id} aria-expanded={openId === r.id} onClick={() => setOpenId(openId === r.id ? null : r.id)}>Tillat ny søknad</Btn>}</td>
                </tr>
                {openId === r.id && <tr><td colSpan={4}><AllowReapply r={r} reasons={data.reapplyReasons || []} noteMax={data.noteMax || 500}
                  onDone={() => { setOpenId(null); reload(); }} /></td></tr>}
              </React.Fragment>
            );
          })}</tbody>
        </table>
      )}
    </Card>
  );
}

/*
 * Medlemsbevis — the mosque's membership confirmation PDF (2026-10-11; renamed 2026-10-15, it was «Medlemsbekreftelser»
 * beside the member's own «Medlemsbekreftelse»): one per finally approved application. A failed one is shown with
 * «Prøv igjen», which makes the document only — the approval and the membership stand either way. This is the
 * MOSQUE's document, not the applicant's signed declaration. `only="open"` is the inbox's group: the ones not made.
 */
const CONF_STATE: Record<string, string> = { pending: 'venter', generating: 'lages', stored: 'laget', failed: 'feilet', not_applicable: 'kan ikke lages' };
const CONF_REASON: Record<string, string> = { no_effective_date: 'registeret har ingen startdato', s3_failed: 'lagringen svarte ikke', store_failed: 'lagringen feilet',
  exists_mismatch: 'en annen fil ligger allerede der', incomplete: 'mangler opplysninger', identifier_in_value: 'et felt ligner et fødselsnummer' };
function Confirmations({ only, onChanged }: { only?: 'open'; onChanged?: () => void }) {
  const { data, error, reload } = useLoad<Body>('/membership-confirmations');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, say] = useMsg();
  // Later pages of stored confirmations ("Vis flere"); reset whenever the first page reloads.
  const [more, setMore] = useState<{ items: Body[]; next: string | null } | null>(null);
  const [paging, setPaging] = useState(false);
  const latest = useRef<Body | null>(null);
  useEffect(() => { latest.current = data; setMore(null); }, [data]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  // A row shown once only, in its newest state: one retried meanwhile (stored now, with its old approval time) may come back on a later page.
  const all: Body[] = latestById([...(data.items || []), ...(more ? more.items : [])] as (Body & { id: string })[]);
  const items = only ? all.filter((c) => c.state !== 'stored') : all;
  const next = more ? more.next : data.nextBefore;
  const openTotal: number = data.openTotal || 0, total: number = data.total || 0;
  const loadMore = async () => {
    if (!next || paging) return;
    setPaging(true);
    const from = data;                                            // the first page this "more" belongs to
    const x = await send(`/membership-confirmations?before=${encodeURIComponent(next)}`);
    setPaging(false);
    if (!x.ok) { say(errText(x.body), 'err'); return; }
    if (latest.current !== from) return;                          // the list reloaded meanwhile: drop this stale page
    setMore((prev) => ({ items: [...(prev ? prev.items : []), ...(x.body.items || [])], next: x.body.nextBefore || null }));
  };
  return (
    <Card title={only ? 'Medlemsbevis som ikke ble laget · ' + openTotal : 'Medlemsbevis · ' + total + (openTotal ? ` (${openTotal} ikke laget)` : '')}
      hint="Moskeens bevis på medlemskap (PDF), laget etter endelig godkjenning."
      more={<p>Det er ikke søkerens signerte innmelding og ikke en BankID-signatur. Mislykkes det, står godkjenningen likevel — prøv igjen.</p>}>
      {!data.on && <div className="card-body">Ikke slått på for denne moskeen. Godkjenninger blir registrert, og bevisene lages når Masjid slår det på.</div>}
      {!items.length ? <div className="card-body">Ingen ennå.</div> : (
        <table className="t">
          <thead><tr><th scope="col">Medlem</th><th scope="col">Godkjent</th><th scope="col">Status</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id}>
              <td>{c.memberName} · medl. {c.memberNo}</td>
              <td>{day10(c.issuedAt)}</td>
              <td><Tag tone={c.state === 'stored' ? 'grey' : c.state === 'failed' ? 'red' : 'gold'}>{CONF_STATE[c.state] || c.state}</Tag>
                {c.reason && <span className="sub" style={{ fontSize: 12, color: 'var(--text-muted)' }}> {CONF_REASON[c.reason] || c.reason}</span>}</td>
              <td>{c.state === 'stored'
                ? <Btn ghost onClick={async () => { const r = await download(`/v1/admin/membership-confirmations/${encodeURIComponent(c.id)}/pdf`, `medlemsbevis-${c.memberNo}.pdf`); if (!r.ok) say(r.message || 'Kunne ikke laste ned.', 'err'); }}>Last ned</Btn>
                : c.canRetry && <Btn ghost disabled={busy === c.id} onClick={async () => {
                    setBusy(c.id);
                    const x = await post(`/membership-confirmations/${encodeURIComponent(c.id)}/retry`, {});
                    setBusy(null);
                    // An older server answered a stored one with no message: never a blank line, and never a word that
                    // sounds as if something was made when it was not (review P3: neutral fallback).
                    say(x.ok ? (x.body.message || (x.body.confirmation && x.body.confirmation.state === 'stored' ? 'Medlemsbeviset er allerede laget.' : 'Status oppdatert.')) : errText(x.body),
                      x.ok && x.body.ok ? 'ok' : 'err'); reload(); onChanged && onChanged();
                  }}>{busy === c.id ? 'Lager …' : 'Prøv igjen'}</Btn>}</td>
            </tr>))}</tbody>
        </table>
      )}
      {total > 0 && !only && <div className="card-body" aria-live="polite">Viser {items.length} av {total}{openTotal ? ` — alle ${openTotal} som ikke er laget vises alltid` : ''}.
        {next && <> <Btn ghost disabled={paging} onClick={loadMore}>{paging ? 'Henter …' : 'Vis flere'}</Btn></>}</div>}
      <Msg text={msg.text} tone={msg.tone} />
    </Card>
  );
}

function AllowReapply({ r, reasons, noteMax, onDone }: { r: Body; reasons: Body[]; noteMax: number; onDone: () => void }) {
  const [reason, setReason] = useState(''); const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const needsNote = reason === 'other';
  useDirty(`reapply-${r.id}`, Boolean(note.trim()));   // PM gaps B11
  return (
    <div className="confirm">
      <div>Søkeren kan sende en ny søknad med en gang. Avslaget står, og ingen blir medlem av dette.</div>
      <div className="acts">
        <Field label="Grunn" required style={{ margin: 0, minWidth: 220 }}>{(id) =>
          <select id={id} value={reason} onChange={(e) => setReason(e.target.value)}><option value="">Velg …</option>{reasons.map((x) => <option key={x.id} value={x.id}>{x.label_no}</option>)}</select>}</Field>
        <Field label={`Notat${needsNote ? ' (påkrevd)' : ' (valgfritt)'}, høyst ${noteMax} tegn — ingen personopplysninger`} style={{ margin: 0, minWidth: 280 }}>{(id) =>
          <input id={id} maxLength={noteMax} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
        <TwoStep label="Tillat ny søknad" confirmLabel="Klikk igjen for å tillate" disabled={busy || !reason || (needsNote && !note.trim())} onConfirm={async () => {
          setBusy(true);
          const x = await post(`/membership-requests/${encodeURIComponent(r.id)}/allow-reapply`, { reason, note });
          setBusy(false);
          if (x.ok) { say(x.body.message || 'Tillatt. Søkeren kan søke igjen.', 'ok'); setTimeout(onDone, 1200); }
          else if (x.body && x.body.code === 'already_allowed') { say('Allerede tillatt.', 'ok'); setTimeout(onDone, 1200); }
          else say(errText(x.body), 'err');
        }} />
      </div>
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/*
 * Approve or reject (owner fix, 2026-10-11). Approval and rejection are two separate forms with their OWN messages:
 * an approval error (e.g. a call-back without its note) is shown beside the approval fields, above «Avslå», and a
 * rejection error only inside the rejection part. The panel checks the approval fields before sending (the server
 * checks them again), names the missing field, marks it, and moves the focus to it. Changing the action or the method
 * clears the old errors; what was typed stays. A form that is sending cannot be sent again.
 */
/** The staff link's limit for an override reason (core member-linking OVERRIDE_REASON_MAX); the server checks it again. */
const OVERRIDE_REASON_MAX = 500;
/** What the approval's answer says about the mosque's medlemsbevis. */
const confirmationNote = (b: Body) => !b.confirmation ? '' : b.confirmation.state === 'stored' ? ' Medlemsbeviset er laget.'
  : b.confirmation.state === 'failed' ? ' Medlemsbeviset kunne ikke lages ennå — prøv igjen under «Til behandling». Godkjenningen står.' : '';
function FieldError({ id, text }: { id: string; text?: string }) {
  useEffect(() => { if (text) fieldErrorToast(text); }, [text]);   // also a toast (2026-10-31)
  return text ? <span className="msg err" id={id} style={{ display: 'block', marginTop: 4 }}>{text}</span> : null;
}

/**
 * One application, decided on its own page (Masjid UX redesign, 2026-10-23; ADM-02, ADM-03, ADM-11).
 *
 *   1 Søkeren          what they gave, and which fields came unchanged from Vipps/BankID
 *   2 Mulige treff     register suggestions with why they match — never proof
 *   3 Hvem er dette?   «Koble til et eksisterende medlem» or «Registrer som ny person», as two choice cards
 *   4 Bekreftelse      how the approver knows it is the right person
 *   → «Godkjenn …» opens a dialog that names the person and says what happens; the result is a receipt that stays.
 *
 * Declining is a SEPARATE mode: «Avslå søknaden …» below the approval swaps the approval steps for the decline form,
 * so approval never shows (or needs) a decline field and the two decisions are never side by side. The rules are the
 * server's and unchanged: four eyes for a staff-flagged application (the approval becomes a proposal), no decision on
 * your own application, «Godkjenn likevel» only when the server offers it, a strong duplicate needs «this is a
 * different person», a call-back needs what the call confirmed (lib/decision).
 */
function Decide({ r, methods, reasons, noteMax, onDone }: { r: Body; methods: Body[]; reasons: Body[]; noteMax: number; onDone: (receipt?: ReceiptData) => void }) {
  const [memberId, setMemberId] = useState<string | null>(null);
  const [pv, setPv] = useState<Body | null>(null);
  const [method, setMethod] = useState(''); const [note, setNote] = useState('');
  const [reason, setReason] = useState(''); const [rnote, setRnote] = useState('');
  const [mode, setMode] = useState<'link' | 'create'>('link');
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  // PM gaps B11: the approval note or the rejection note typed and not sent — leaving asks first.
  useDirty(`decide-${r.id}`, Boolean(note.trim() || (decision === 'reject' && rnote.trim())));
  const [notDuplicate, setNotDuplicate] = useState(false);
  // «Godkjenn likevel» (owner, 2026-10-15): offered only when the SERVER says this person may override (admin tier,
  // access.canOverrideMemberLink) after an approval was refused because another account holds the person.
  const [overrideOffer, setOverrideOffer] = useState(false);
  const [dialog, setDialog] = useState<null | 'approve' | 'override' | 'reject' | 'confirm'>(null);
  const [amsg, sayA] = useMsg(); const [rmsg, sayR] = useMsg();
  const [aerr, setAerr] = useState<Partial<Record<ApproveField, string>>>({});
  const [rerr, setRerr] = useState<{ reason?: string; note?: string }>({});
  const [needNote, setNeedNote] = useState(false);
  const refs = { memberId: useRef<HTMLDivElement>(null), method: useRef<HTMLSelectElement>(null), note: useRef<HTMLInputElement>(null), notDuplicate: useRef<HTMLInputElement>(null) };
  const rreason = useRef<HTMLSelectElement>(null); const rnoteRef = useRef<HTMLTextAreaElement>(null);
  const uid = 'd-' + String(r.id).replace(/[^A-Za-z0-9_-]/g, '');
  useEffect(() => {
    if (!memberId || !r.userId) return;
    setPv(null);
    // Review F5: only the answer for the person chosen NOW is used — a slow answer for an earlier click must never name
    // the wrong person on the button and in the dialog. Review F4: a failed preview lets go of the choice, so the search
    // comes back instead of «Henter …» forever.
    let current = true;
    void send(`/members/link-preview?userId=${encodeURIComponent(r.userId)}&memberId=${encodeURIComponent(memberId)}`)
      .then((x) => { if (!current) return; if (x.ok) setPv(x.body); else { setMemberId(null); sayA(errText(x.body), 'err'); } });
    return () => { current = false; };
  }, [memberId, r.userId]);
  const reg = r.registration;
  const applicant = nameOf(r);
  const candidates: Body[] = r.candidates || [];
  const strong = candidates.some((c: Body) => c.strength === 'strong');
  const noteRequired = method === 'called_back';
  const inv = (k: ApproveField) => (aerr[k] ? true : undefined);
  const eid = (k: ApproveField) => `${uid}-${k}-err`;
  const changeApproval = (fn: () => void) => { fn(); setAerr({}); sayA(''); setOverrideOffer(false); };
  const showApprovalErrors = (e: Partial<Record<ApproveField, string>>, text?: string) => {
    setAerr(e);
    const first = APPROVE_FIELDS.find((k) => e[k]);
    sayA(text || 'Godkjenningen er ikke sendt. ' + APPROVE_FIELDS.filter((k) => e[k]).map((k) => e[k]).join(' '), 'err');
    if (first) setTimeout(() => refs[first].current?.focus(), 0);
  };
  /** «Godkjenn …»: the form's own check first (lib/decision), then the dialog. Nothing is sent from here. */
  const askApprove = () => {
    sayR('');
    const e = approvalErrors({ mode, memberId, method, note, strong, notDuplicate, previewReady: Boolean(pv) });
    if (Object.keys(e).length) { showApprovalErrors(e); return; }
    setAerr({}); sayA(''); setDialog('approve');
  };
  const memberHref = (id?: string | null) => (id ? '#/medlemmer/person/' + encodeURIComponent(id) : undefined);
  const approvedReceipt = (x: Sent, linkedTo: string | null): ReceiptData => {
    if (x.status === 202 || x.body.awaitingConfirmation) return { id: r.id + ':p', tone: 'wait', title: `Godkjenningen av ${applicant} er foreslått`,
      lines: [x.body.message || 'En annen ansatt må bekrefte før medlemskapet begynner.', 'Søknaden står i listen til den er bekreftet. ' + receiptTime()] };
    const mid = x.body.member?.id || x.body.memberId || linkedTo;
    const href = memberHref(mid);
    return { id: r.id + ':a', tone: 'ok', title: `${applicant} er godkjent som medlem`,
      lines: [(x.body.message || 'Godkjent.') + confirmationNote(x.body), 'Godkjent av deg ' + receiptTime() + '. Beslutningen er loggført.'],
      action: href ? { label: 'Åpne personen i registeret', href } : undefined };
  };
  /** Sends an approval from the dialog. Field errors close the dialog and point at the field; others stay in it. */
  const approve = async (body: Body): Promise<ConfirmResult> => {
    const x = await post(`/membership-requests/${encodeURIComponent(r.id)}/approve`, body);
    if (x.ok) { setDialog(null); onDone(approvedReceipt(x, body.memberId || null)); return { ok: true }; }
    const fe: Partial<Record<ApproveField, string>> = {};
    const errs = (x.body && x.body.errors) || {};
    for (const [k, v] of Array.isArray(errs) ? errs.map((o: Body) => [o.field, o.message]) : Object.entries(errs)) if (APPROVE_FIELDS.includes(k as ApproveField)) fe[k as ApproveField] = String(v);
    if (x.body && x.body.code === 'duplicate_confirmation_required') fe.notDuplicate = 'Bekreft at du har sjekket forslagene og at dette er en annen person.';
    const text = errText(x.body);
    if (x.body && x.body.code === 'already_linked' && x.body.canOverride === true) { setDialog(null); setOverrideOffer(true); sayA(text, 'err'); return { ok: true }; }
    if (Object.keys(fe).length) { setDialog(null); showApprovalErrors(fe, text); return { ok: true }; }
    return { ok: false, error: text };
  };
  const confirmProposal = async (): Promise<ConfirmResult> => {
    if (needNote && !note.trim()) { setDialog(null); setAerr({ note: 'Skriv kort hva som ble bekreftet i samtalen.' }); setTimeout(() => refs.note.current?.focus(), 0); return { ok: true }; }
    const x = await post(`/membership-requests/${encodeURIComponent(r.id)}/confirm`, needNote ? { note } : {});
    if (x.ok) { setDialog(null); onDone({ id: r.id + ':c', tone: 'ok', title: `${applicant} er godkjent som medlem`,
      lines: [(x.body.message || 'Godkjent.') + confirmationNote(x.body), 'Bekreftet av deg ' + receiptTime() + '. Begge navnene er loggført.'] }); return { ok: true }; }
    if (x.body && x.body.errors && x.body.errors.note) { setDialog(null); setNeedNote(true); setAerr({ note: String(x.body.errors.note) }); sayA(errText(x.body), 'err'); setTimeout(() => refs.note.current?.focus(), 0); return { ok: true }; }
    return { ok: false, error: errText(x.body) };
  };
  /** «Avslå søknaden»: reason (and a note for «Annet») checked here, then the dialog. */
  const askReject = () => {
    const e: { reason?: string; note?: string } = {};
    if (!reason) e.reason = 'Velg en grunn.';
    else if (reason === 'other' && !rnote.trim()) e.note = 'Skriv kort hvorfor, når grunnen er «Annet».';
    setRerr(e);
    if (e.reason) { sayR('Avslaget er ikke sendt. ' + e.reason, 'err'); rreason.current?.focus(); return; }
    if (e.note) { sayR('Avslaget er ikke sendt. ' + e.note, 'err'); rnoteRef.current?.focus(); return; }
    sayR(''); setDialog('reject');
  };
  const rejectIt = async (): Promise<ConfirmResult> => {
    const x = await post(`/membership-requests/${encodeURIComponent(r.id)}/reject`, { reason, note: rnote });
    if (!x.ok) return { ok: false, error: errText(x.body) };
    setDialog(null);
    onDone({ id: r.id + ':r', tone: 'info', title: `Søknaden fra ${applicant} er avslått`,
      lines: ['Søkeren ser at søknaden ikke ble godkjent. Grunnen og notatet ser bare dere.',
        'Avslått av deg ' + receiptTime() + '. Ombestemmer dere dere, kan en administrator velge «Tillat ny søknad» under «Avslåtte søknader».'] });
    return { ok: true };
  };
  const reasonLabel = (reasons.find((x) => x.id === reason) || { label_no: '' }).label_no;
  const methodLabel = (methods.find((m) => m.id === method) || { label_no: method }).label_no;

  if (r.selfDecision) return <div className="card-body"><div className="warnbox"><b>Denne søknaden gjelder deg selv.</b> En annen ansatt må behandle den.</div></div>;
  if (r.proposal) return (
    <div className="card-body decide">
      <div className="warnbox"><b>Godkjenning foreslått{r.proposal.mode === 'create' ? ' – registrer som ny person' : r.proposal.override ? ' – godkjenn likevel' : ' – koble til en person i registeret'}.</b>{' '}
        Søknaden kan gjelde en ansatt, så en annen ansatt må bekrefte før medlemskapet begynner.</div>
      {r.proposal.method && <p>Den som foreslo, bekreftet hvem søkeren er slik: <b>{(methods.find((m) => m.id === r.proposal.method) || { label_no: r.proposal.method }).label_no}</b>{r.proposal.hasNote ? ' (med notat)' : ''}.</p>}
      {r.proposal.byMe ? <p>Du foreslo godkjenningen. En annen ansatt må bekrefte den.</p> : <>
        {needNote && <Field label="Hva bekreftet personen i samtalen?" required>{(id) => <>
          <input id={id} ref={refs.note} value={note} aria-required aria-invalid={inv('note')} aria-describedby={aerr.note ? eid('note') : undefined}
            onChange={(e) => { setNote(e.target.value); if (aerr.note) { setAerr({}); sayA(''); } }} />
          <FieldError id={eid('note')} text={aerr.note} /></>}</Field>}
        <div className="decide-go"><Btn small={false} onClick={() => setDialog('confirm')}>Bekreft godkjenningen</Btn></div></>}
      <Msg text={amsg.text} tone={amsg.tone} />
      <ConfirmDialog open={dialog === 'confirm'} title="Bekreft godkjenningen" subject={applicant} confirmLabel="Bekreft – godkjenn søknaden"
        effects={[`${applicant} blir medlem.`, 'Du bekrefter det en annen ansatt foreslo. Begge navnene loggføres.']}
        onConfirm={confirmProposal} onClose={() => setDialog(null)} />
    </div>
  );
  const linkable = r.accountExists && can('members.link');
  return (
    <div className="card-body decide">
      {r.staffFlagged && <div className="warnbox"><b>Må bekreftes av en annen ansatt.</b> Søknaden kan gjelde en ansatt (et varsel, ikke bevis). Når du godkjenner, blir det et forslag som en annen ansatt bekrefter.</div>}
      <section className="step" aria-labelledby={uid + '-s1'}>
        <h4 id={uid + '-s1'}><span className="step-n" aria-hidden="true">1</span>Søkeren</h4>
        {reg ? <dl className="app-dl">
          <div><dt>Navn</dt><dd>{reg.firstName} {reg.lastName} <Same k="firstName" r={r} /></dd></div>
          <div><dt>Fødselsdato</dt><dd>{dotDate(reg.birthDate)} <Same k="birthDate" r={r} /></dd></div>
          {reg.phone && <div><dt>Telefon</dt><dd>{reg.phone} <Same k="phone" r={r} /></dd></div>}
          {reg.email && <div><dt>E-post</dt><dd>{reg.email} <Same k="email" r={r} /></dd></div>}
          {reg.addressLine && <div><dt>Adresse</dt><dd>{reg.addressLine}, {reg.postcode || ''} {reg.city || ''}</dd></div>}
          {typeof r.nationalIdGiven === 'boolean' && <div><dt>Fødselsnummer</dt><dd>{r.nationalIdGiven ? 'oppgitt' : 'ikke oppgitt'}</dd></div>}
        </dl> : <p>Søknaden ble sendt fra en eldre app uten skjema. Bare innloggingen er kjent: <AccountIdentity account={r.account} exists={r.accountExists} />.</p>}
      </section>
      {decision === 'approve' ? <>
        {!linkable && <div className="warnbox">{r.accountExists ? 'For å godkjenne trenger du også tilgangen «Koble medlemmer». Du kan avslå søknaden nedenfor, eller be en med tilgang om å godkjenne.' : 'Søkerens konto er slettet, så søknaden kan ikke godkjennes. Du kan avslå den nedenfor.'}</div>}
        {linkable && <>
          <section className="step" aria-labelledby={uid + '-s2'}>
            <h4 id={uid + '-s2'}><span className="step-n" aria-hidden="true">2</span>Mulige treff i registeret</h4>
            {candidates.length ? <>
              <p className="note">{r.waitingFor === 'duplicate' ? 'Innmeldingen stoppet her fordi registeret kan ha denne personen fra før. ' : ''}Forslag ut fra navn, fødselsdato og kontaktinfo. Et forslag er ikke bevis — sjekk før du velger.</p>
              <ul className="cand-list">{candidates.map((c: Body) => {
                const on = mode === 'link' && memberId === c.memberId;
                return <li key={c.memberId} className={'cand' + (on ? ' on' : '')}>
                  <span><b>{c.name}</b> · medl. {c.memberNo || c.solidusNo}<br /><span className="note">{(c.reasons_no || []).join(' · ')}{c.strength === 'strong' ? ' — sterkt treff' : ''}</span></span>
                  <Btn ghost={!on} aria-pressed={on} aria-label={`Velg ${c.name}, medlemsnummer ${c.memberNo || c.solidusNo}`} onClick={() => changeApproval(() => { setMode('link'); setMemberId(c.memberId); })}>{on ? 'Valgt' : 'Velg denne personen'}</Btn>
                </li>; })}</ul>
            </> : <p>Ingen i registeret ligner søkeren. Søk selv hvis du tror personen finnes, ellers registrer en ny person.</p>}
          </section>
          <fieldset className="step" aria-describedby={aerr.memberId ? eid('memberId') : undefined}>
            <legend><span className="step-n" aria-hidden="true">3</span>Hvem er dette?</legend>
            <div className="choice-cards">
              <label className={'choice' + (mode === 'link' ? ' on' : '')}>
                <input type="radio" name={uid + '-mode'} checked={mode === 'link'} onChange={() => changeApproval(() => setMode('link'))} />
                <span><b>Koble til et eksisterende medlem</b><span>Personen står allerede i registeret. Søkeren får tilgang til det medlemskapet.</span></span>
              </label>
              {reg && <label className={'choice' + (mode === 'create' ? ' on' : '')}>
                <input type="radio" name={uid + '-mode'} checked={mode === 'create'} onChange={() => changeApproval(() => { setMode('create'); setMemberId(null); setPv(null); })} />
                <span><b>Registrer som ny person</b><span>Personen får et nytt medlemsnummer (OA-…) ut fra det søkeren oppga.</span></span>
              </label>}
            </div>
            {mode === 'link' && <div ref={refs.memberId} tabIndex={-1} className="step-inner">
              {!memberId && <PersonSearch label="Finn personen: navn eller medlemsnummer" onPick={(m) => changeApproval(() => setMemberId(m.id))} />}
              {memberId && !pv && <Loading what="Henter hva personen får se …" />}
              {pv && <div className="pv-box"><LinkPreview pv={pv} /></div>}
              {memberId && <button type="button" className="link" onClick={() => changeApproval(() => { setMemberId(null); setPv(null); })}>Velg en annen person</button>}
              <FieldError id={eid('memberId')} text={aerr.memberId} />
            </div>}
            {mode === 'create' && <div className="step-inner">
              <p>Medlemskapet regnes fra dagen søknaden ble sendt ({osloDay(r.requestedAt)}).</p>
              {strong && <div className="warnbox"><label className="check"><input ref={refs.notDuplicate} type="checkbox" checked={notDuplicate} aria-invalid={inv('notDuplicate')}
                aria-describedby={aerr.notDuplicate ? eid('notDuplicate') : undefined} onChange={(e) => changeApproval(() => setNotDuplicate(e.target.checked))} /> Jeg har sjekket forslagene over: dette er en annen person (blir loggført)</label>
                <FieldError id={eid('notDuplicate')} text={aerr.notDuplicate} /></div>}
            </div>}
          </fieldset>
          <section className="step" aria-labelledby={uid + '-s4'}>
            <h4 id={uid + '-s4'}><span className="step-n" aria-hidden="true">4</span>Bekreft hvem søkeren er</h4>
            <div className="acts">
              <MethodField methods={methods} value={method} selectRef={refs.method} error={aerr.method} errId={eid('method')}
                onChange={(v) => changeApproval(() => setMethod(v))} />
              <Field label={noteRequired ? 'Hva bekreftet personen i samtalen?' : 'Notat (valgfritt)'} required={noteRequired} style={{ margin: 0, minWidth: 260 }}>{(id) => <>
                <input id={id} ref={refs.note} value={note} aria-required={noteRequired || undefined} aria-invalid={inv('note')}
                  aria-describedby={aerr.note ? eid('note') : undefined} placeholder={noteRequired ? 'F.eks. bekreftet navn og fødselsdato' : ''}
                  onChange={(e) => { setNote(e.target.value); if (aerr.note) { setAerr({ ...aerr, note: undefined }); sayA(''); } }} />
                <FieldError id={eid('note')} text={aerr.note} /></>}</Field>
            </div>
          </section>
          <Msg text={amsg.text} tone={amsg.tone} />
          <div className="decide-go">
            <Btn small={false} onClick={askApprove}>{mode === 'create' ? 'Godkjenn og registrer som ny person' : pv ? 'Godkjenn og koble til ' + pv.name : 'Godkjenn søknaden'}</Btn>
          </div>
          {overrideOffer && mode === 'link' && <div className="warnbox">
            <b>Personen er allerede koblet til en annen konto.</b> Som administrator kan du godkjenne likevel: også denne kontoen får tilgang, og den andre beholder sin.
            <div className="decide-go"><Btn small={false} onClick={() => setDialog('override')}>Godkjenn likevel …</Btn></div>
          </div>}
        </>}
        <div className="decide-alt">
          <span>Skal søknaden ikke godkjennes?</span>
          <Btn ghost onClick={() => { setDecision('reject'); sayA(''); setAerr({}); setTimeout(() => rreason.current?.focus(), 0); }}>Avslå søknaden …</Btn>
        </div>
      </> : (
        <section className="step reject" aria-labelledby={uid + '-rj'}>
          <h4 id={uid + '-rj'}>Avslå søknaden</h4>
          <p>Søkeren får en generell melding om at søknaden ikke ble godkjent. Grunnen og notatet ser bare dere.</p>
          <div className="acts">
            <Field label="Grunn" required style={{ margin: 0, minWidth: 240 }}>{(id) =>
              <select id={id} ref={rreason} value={reason} aria-invalid={rerr.reason ? true : undefined} onChange={(e) => { setReason(e.target.value); setRerr({}); sayR(''); }}>
                <option value="">Velg …</option>{reasons.map((x) => <option key={x.id} value={x.id}>{x.label_no}</option>)}</select>}</Field>
            <Field label={reason === 'other' ? `Hvorfor? (høyst ${noteMax} tegn)` : `Internt notat (valgfritt, høyst ${noteMax} tegn)`} required={reason === 'other'} note="Ingen personopplysninger." style={{ margin: 0, minWidth: 280, flex: 1 }}>{(id, d) =>
              <textarea id={id} ref={rnoteRef} rows={2} maxLength={noteMax} value={rnote} aria-describedby={d} aria-invalid={rerr.note ? true : undefined}
                onChange={(e) => { setRnote(e.target.value); setRerr({}); }} />}</Field>
          </div>
          <Msg text={rmsg.text} tone={rmsg.tone} />
          <div className="decide-go">
            <Btn small={false} danger onClick={askReject}>Avslå søknaden</Btn>
            <Btn small={false} ghost onClick={() => { setDecision('approve'); sayR(''); setRerr({}); }}>Tilbake til godkjenning</Btn>
          </div>
        </section>
      )}
      <ConfirmDialog open={dialog === 'approve'} title={mode === 'create' ? 'Godkjenn og registrer ny person' : 'Godkjenn søknaden'}
        subject={mode === 'create' ? applicant : `${applicant} → ${pv ? pv.name + ' · medl. ' + (pv.memberNo || pv.solidusNo) : ''}`}
        confirmLabel={r.staffFlagged ? 'Send forslaget' : 'Godkjenn'}
        effects={[
          ...(mode === 'create'
            ? [`${applicant} registreres som ny person med eget medlemsnummer (OA-…).`, `Medlemskapet regnes fra ${osloDay(r.requestedAt)}, dagen søknaden ble sendt.`]
            : [`Søkerens konto kobles til ${pv ? pv.name : 'personen'} i registeret, og søknaden er godkjent.`, ...(pv?.grants_no || []).map((g: string) => 'Personen får se: ' + g)]),
          `Du bekrefter hvem søkeren er slik: ${methodLabel}.`,
          r.staffFlagged ? 'Fordi søknaden kan gjelde en ansatt, blir dette et forslag. Medlemskapet begynner først når en annen ansatt bekrefter.' : 'Beslutningen loggføres med ditt navn.',
        ]}
        onConfirm={() => approve(mode === 'create' ? { mode: 'create', method, note, notDuplicate } : { memberId, method, note })} onClose={() => setDialog(null)} />
      <ConfirmDialog open={dialog === 'override'} title="Godkjenn likevel" subject={`${applicant} → ${pv ? pv.name : ''}`} confirmLabel="Godkjenn likevel"
        effects={['Også denne kontoen får tilgang til personen. Den andre kontoen beholder sin tilgang.', 'Begrunnelsen loggføres med ditt navn.']}
        reason={{ label: `Begrunnelse (høyst ${OVERRIDE_REASON_MAX} tegn)`, required: true, max: OVERRIDE_REASON_MAX, hint: 'F.eks. egen konto; husstandskortet beholdes.' }}
        onConfirm={(why) => approve({ memberId, method, note, override: true, reason: why })} onClose={() => setDialog(null)} />
      <ConfirmDialog open={dialog === 'reject'} danger title="Avslå søknaden" subject={applicant} confirmLabel="Avslå søknaden"
        effects={['Søknaden avslås. Søkeren ser bare at den ikke ble godkjent.', `Grunn (bare for dere): ${reasonLabel}${rnote.trim() ? ' — med notat' : ''}.`,
          'Søkeren kan søke igjen etter 30 dager. En administrator kan tillate en ny søknad før det.']}
        onConfirm={rejectIt} onClose={() => setDialog(null)} />
    </div>
  );
}

/** «samme som BankID» or «endret av søkeren» beside a field the sign-in also knew. Nothing for a field it did not. */
function Same({ k, r }: { k: string; r: Body }) {
  const m = r.providerMatch as Record<string, boolean> | undefined;
  if (!m || !(k in m)) return null;
  const who = PROVIDER_NAME[r.account?.identity?.provider] || 'innloggingen';
  return m[k] ? <Tag tone="grey">samme som {who}</Tag> : <Tag tone="gold">endret av søkeren</Tag>;
}
/** 1985-11-23 → 23.11.1985. Anything else is shown as it came. */
const dotDate = (v?: string | null) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[3]}.${m[2]}.${m[1]}` : String(v || ''); };

function CompleteRemoval({ r, onDone }: { r: Body; onDone: () => void }) {
  const [msg, say] = useMsg();
  return (
    <div className="confirm">
      <div>{r.member ? <><b>{r.member.name}</b> · medl. {r.member.memberNo || r.member.solidusNo} </> : null}ba om utmelding før utmelding i appen begynte å gjelde med en gang. Utmeldingen gjelder fra den dagen den ble sendt — ikke fra i dag. Meld personen ut i medlemsregisteret også, hvis personen kom derfra.</div>
      <div className="acts">
        <TwoStep label="Fullfør utmelding" confirmLabel="Klikk igjen for å fullføre" onConfirm={async () => {
          const x = await post(`/membership-requests/${encodeURIComponent(r.id)}/complete-removal`, {});
          if (x.ok) { say(x.body.message || 'Utmeldt.', 'ok'); setTimeout(onDone, 900); } else say(errText(x.body), 'err');
        }} />
      </div>
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}
